import React, { Fragment, useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Pill } from "../pill";

export type InboxFilterChip = {
  label: string;
  value: string;
};

export type InboxFilterOption = {
  label: string;
  value: string;
};

export interface InboxFilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchClear?: () => void;
  placeholder?: string;
  activeFilters?: InboxFilterChip[];
  onRemoveFilter?: (value: string) => void;
  filterOptions: InboxFilterOption[];
  selectedFilterValue: string;
  onFilterSelect: (value: string) => void;
  className?: string;
}

const inputClasses =
  "h-12 w-full rounded-xl border border-neutral-200 bg-white pl-11 pr-12 text-base text-neutral-800 placeholder:text-neutral-500 shadow-[0px_1px_2px_rgba(17,23,41,0.08)] focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-100 focus-visible:outline-none";

const menuButtonClasses =
  "inline-flex h-12 items-center gap-2 rounded-xl border border-neutral-200 bg-white px-5 text-base font-medium text-neutral-800 shadow-[0px_1px_2px_rgba(17,23,41,0.08)] hover:bg-neutral-0 focus-visible:border-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-100";

const menuItemBase =
  "flex w-full items-center justify-between gap-2 rounded-lg px-4 py-2 text-sm text-neutral-800 hover:bg-primary-50";

export const InboxFilterBar = ({
  searchValue,
  onSearchChange,
  onSearchClear,
  placeholder = "Search Emails",
  activeFilters = [],
  onRemoveFilter,
  filterOptions,
  selectedFilterValue,
  onFilterSelect,
  className,
}: InboxFilterBarProps) => {
  const inputId = useMemo(() => `inbox-filter-${Math.random().toString(36).slice(2, 9)}`, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  const handleClear = () => {
    if (onSearchClear) {
      onSearchClear();
    } else {
      onSearchChange("");
    }
  };

  return (
    <div className={clsx("tw-boundary w-full", className)}>
      <div className="flex w-full flex-wrap gap-3 md:flex-nowrap md:items-center">
        <div className="relative flex-1 min-w-[240px]">
          <label htmlFor={inputId} className="sr-only">
            Search emails
          </label>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-500" />
          <input
            id={inputId}
            type="search"
            value={searchValue}
            placeholder={placeholder}
            className={inputClasses}
            onChange={handleSearchChange}
          />
          {searchValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-neutral-500 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        <Menu as="div" className="relative inline-block text-left">
          {({ open }) => (
            <>
              <Menu.Button className={menuButtonClasses} aria-haspopup="menu" aria-expanded={open}>
                Filter By
                <ChevronDownIcon className="h-4 w-4 text-neutral-600" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-xl border border-neutral-200 bg-white p-2 shadow-lg focus:outline-none">
                  {filterOptions.map((option) => {
                    const isSelected = option.value === selectedFilterValue;
                    return (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={clsx(
                              menuItemBase,
                              active && "bg-primary-50 text-primary-800",
                              isSelected && "font-semibold",
                            )}
                            onClick={() => onFilterSelect(option.value)}
                          >
                            <span>{option.label}</span>
                            {isSelected && <CheckIcon className="h-4 w-4 text-primary-600" />}
                          </button>
                        )}
                      </Menu.Item>
                    );
                  })}
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      </div>

      {activeFilters.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-y-2 -ml-2">
          {activeFilters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              className="inline-flex items-center bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 rounded-full"
              onClick={() => onRemoveFilter?.(filter.value)}
              aria-label={`Remove ${filter.label} filter`}
            >
              <Pill variant="info">
                <span className="flex items-center gap-1">
                  {filter.label}
                  <XMarkIcon className="h-4 w-4" />
                </span>
              </Pill>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InboxFilterBar;
